import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Chess } from 'chess.js';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

import { Loader } from '@/components/common/Loader';
import { cn } from '@/lib/cn';

/** FEN piece char → bundled cburnett piece SVG (public/pieces/{w|b}{K…P}.svg). */
function pieceSrc(p: string): string {
  return `/pieces/${p === p.toUpperCase() ? 'w' : 'b'}${p.toUpperCase()}.svg`;
}

interface ChessReplayProps {
  gameId: string;
  /** Viewer's Lichess username — orients the board with their side at bottom. */
  myUsername?: string;
}

/** FEN piece-placement → 8×8 rows (rank 8 first, file a first). */
function fenToRanks(fen: string): (string | null)[][] {
  return fen
    .split(' ')[0]
    .split('/')
    .map((rank) => {
      const row: (string | null)[] = [];
      for (const ch of rank) {
        if (ch >= '1' && ch <= '8') {
          for (let i = 0; i < Number(ch); i++) row.push(null);
        } else {
          row.push(ch);
        }
      }
      return row;
    });
}

/**
 * Self-rendered Lichess game replay. Fetches the PGN directly from Lichess
 * (CORS-open game-export API — available immediately, unlike the iframe game
 * embed which lags until Lichess finalises a freshly-resigned game), parses it
 * with chess.js, and renders a full-width board with move navigation. Reliable
 * (no embed finalisation lag) and sized large for a comfortable replay.
 */
export function ChessReplay({ gameId, myUsername }: ChessReplayProps) {
  const [pgn, setPgn] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [ply, setPly] = useState(0);

  useEffect(() => {
    let off = false;
    setPgn(null);
    setFailed(false);
    setPly(0);
    fetch(
      `https://lichess.org/game/export/${encodeURIComponent(gameId)}?moves=true&clocks=false&evals=false&literate=false`,
      { headers: { Accept: 'application/x-chess-pgn' } },
    )
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((txt) => {
        if (!off) setPgn(txt);
      })
      .catch(() => {
        if (!off) setFailed(true);
      });
    return () => {
      off = true;
    };
  }, [gameId]);

  const game = useMemo(() => {
    if (!pgn) return null;
    try {
      const c = new Chess();
      c.loadPgn(pgn);
      const sans = c.history();
      const r = new Chess();
      const fens = [r.fen()];
      for (const san of sans) {
        r.move(san);
        fens.push(r.fen());
      }
      const black = /\[Black "([^"]*)"\]/.exec(pgn)?.[1] ?? '';
      const orientation: 'white' | 'black' =
        myUsername && black.toLowerCase() === myUsername.toLowerCase() ? 'black' : 'white';
      return { fens, sans, orientation };
    } catch {
      return null;
    }
  }, [pgn, myUsername]);

  if (failed || (pgn !== null && game === null)) {
    return (
      <a
        href={`https://lichess.org/${gameId}`}
        target="_blank"
        rel="noreferrer"
        className="block rounded-lg bg-bg-secondary px-3 py-3 text-center text-sm font-semibold text-brand underline"
      >
        lichess.org/{gameId}
      </a>
    );
  }
  if (!game) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader />
      </div>
    );
  }

  const total = game.fens.length - 1;
  const cells = fenToRanks(game.fens[ply]).flatMap((row, r) =>
    row.map((p, f) => ({ p, r, f })),
  );
  const ordered = game.orientation === 'white' ? cells : [...cells].reverse();
  const lastSan = ply > 0 ? game.sans[ply - 1] : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-8 overflow-hidden rounded-lg border border-border">
        {ordered.map(({ p, r, f }) => {
          const dark = (r + f) % 2 === 1;
          return (
            <div
              key={`${r}-${f}`}
              className="aspect-square"
              style={{ backgroundColor: dark ? '#b58863' : '#f0d9b5' }}
            >
              {p && (
                <img
                  src={pieceSrc(p)}
                  alt=""
                  draggable={false}
                  className="h-full w-full select-none"
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1">
          <NavBtn onClick={() => setPly(0)} disabled={ply === 0}>
            <ChevronsLeft aria-hidden className="h-4 w-4" />
          </NavBtn>
          <NavBtn onClick={() => setPly((p) => Math.max(0, p - 1))} disabled={ply === 0}>
            <ChevronLeft aria-hidden className="h-4 w-4" />
          </NavBtn>
        </div>
        <span className="truncate text-[11px] tabular-nums text-fg-muted">
          {lastSan ?? 'Start'} · {ply}/{total}
        </span>
        <div className="flex gap-1">
          <NavBtn onClick={() => setPly((p) => Math.min(total, p + 1))} disabled={ply >= total}>
            <ChevronRight aria-hidden className="h-4 w-4" />
          </NavBtn>
          <NavBtn onClick={() => setPly(total)} disabled={ply >= total}>
            <ChevronsRight aria-hidden className="h-4 w-4" />
          </NavBtn>
        </div>
      </div>
    </div>
  );
}

function NavBtn({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex h-9 w-10 items-center justify-center rounded-lg bg-bg-tertiary text-fg',
        'transition-opacity active:opacity-80 disabled:opacity-40',
      )}
    >
      {children}
    </button>
  );
}
